import {ConfirmationDialog, FormControl, IconButton, Link, Textarea} from '@primer/react'
import {ScreenFullIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import styles from './ConfigSections.module.css'
import {SectionHeader} from './SectionHeader'

type PromptSectionProps = {
  prompt: string
  onChange: (prompt: string) => void
  showValidation: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  defaultPrompt?: string
}

export function PromptSection({
  prompt,
  onChange,
  showValidation,
  isExpanded,
  onToggleExpand,
  defaultPrompt,
}: PromptSectionProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  const isDefault = defaultPrompt !== undefined && prompt.trim() === defaultPrompt.trim()

  const handleResetToDefault = useCallback(() => {
    if (defaultPrompt !== undefined) {
      onChange(defaultPrompt)
    }
    setShowResetConfirm(false)
  }, [defaultPrompt, onChange])

  const resetLink =
    defaultPrompt !== undefined && !isDefault ? (
      <Link
        as="button"
        type="button"
        onClick={() => setShowResetConfirm(true)}
        data-testid="reset-prompt-button"
        className={styles.resetLink}
      >
        Reset to default
      </Link>
    ) : null

  return (
    <div className={clsx(styles.section, isExpanded && styles.sectionExpanded)}>
      {!isExpanded && (
        <SectionHeader
          title="Prompt"
          subtitle="High-level instructions — set overall goals, tone, and guardrails."
          action={
            <div className={styles.actionStack}>
              <IconButton
                icon={ScreenFullIcon}
                aria-label="Expand prompt"
                variant="invisible"
                size="small"
                onClick={onToggleExpand}
                data-testid="prompt-expand-button"
              />
              {resetLink}
            </div>
          }
        />
      )}
      {isExpanded && (
        <SectionHeader
          hideTitleRow
          subtitle="High-level instructions — set overall goals, tone, and guardrails."
          action={resetLink}
        />
      )}

      {showValidation && !prompt.trim() && <p className={styles.validationError}>Prompt instructions are required.</p>}

      <FormControl>
        <FormControl.Label visuallyHidden>Prompt instructions</FormControl.Label>
        <Textarea
          className={clsx(styles.promptTextarea, isExpanded && styles.promptTextareaExpanded)}
          rows={isExpanded ? 25 : 7}
          value={prompt}
          onChange={e => onChange(e.target.value)}
          aria-label="Prompt instructions"
          data-testid="prompt-textarea"
          resize="none"
          block
        />
      </FormControl>

      {showResetConfirm && (
        <ConfirmationDialog
          title="Reset prompt to default?"
          onClose={gesture => {
            if (gesture === 'confirm') {
              handleResetToDefault()
            } else {
              setShowResetConfirm(false)
            }
          }}
          confirmButtonContent="Reset"
          confirmButtonType="danger"
          cancelButtonContent="Cancel"
        >
          This will replace your current prompt with the default template. This change won&apos;t be saved until you
          commit.
        </ConfirmationDialog>
      )}
    </div>
  )
}
